/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { isEmpty, typeOf } from '@ember/utils';
import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { SafeString } from 'handlebars';
import { keyPathToString } from 'cio/utils/objects';

interface TriggerJsonFormatterItemArgs {
  item: { key: string | number; value: string | number | any; isArrayItem?: boolean };
  pathsUsed: string[];
  depth: number;
  path?: string[];
  index?: number;
  onItemClick: (value: string, event?: Event) => unknown;
  canClickItem: boolean;
  highlightedPath: string | null;
  iconOffsetStyle: SafeString;
  collapse: boolean;
  mouseEvent: 'click' | 'mousedown';
  itemPaddingLeft: number;
}

export default class TriggerJsonFormatterItem extends Component<TriggerJsonFormatterItemArgs> {
  @tracked isExpanded = !this.args.collapse;

  @cached
  get isHighlighted() {
    if (this.args.highlightedPath == null) {
      return false;
    }

    return this.args.highlightedPath === this.pathString;
  }

  @cached
  get paddingStyle() {
    return htmlSafe(`padding-left: ${(this.args.depth + 1) * 16 + this.args.itemPaddingLeft}px;`);
  }

  @cached
  get indents() {
    if (this.args.depth < 1) {
      return [];
    }

    return [...new Array(this.args.depth - 1)].map((_, index) => htmlSafe(`--leftOffset: -${(index + 1) * 16}px;`));
  }

  @cached
  get shouldShowKey() {
    return !isEmpty(this.args.item.key) && (this.args.item.isArrayItem != null ? !this.args.item.isArrayItem : true);
  }

  @cached
  get valueType() {
    return typeOf(this.args.item.value);
  }

  @cached
  get valueToDisplay() {
    switch (this.valueType) {
      case 'string':
        return `"${this.args.item.value}"`;
      case 'number':
        return this.args.item.value;
      case 'null':
        return 'null';
      default:
        return this.args.item.value;
    }
  }

  @cached
  get valueColorTypeClass() {
    let classes = ['trigger-json-formatter-item-value'];

    switch (this.valueType) {
      case 'string':
        classes.push('trigger-json-formatter-item-value--string');
        break;
      case 'number':
        classes.push('trigger-json-formatter-item-value--number');
        break;
      case 'null':
        classes.push('trigger-json-formatter-item-value--keyword');
        break;
      case 'boolean':
        classes.push('trigger-json-formatter-item-value--boolean');
        break;
    }

    return classes.join(' ');
  }

  @cached
  get isExpandable() {
    if (this.valueType === 'array') {
      return this.args.item.value.length > 0;
    }

    // @ts-ignore typeOf isn't typed correctly
    if (this.valueType === 'object') {
      return Object.keys(this.args.item.value).length > 0;
    }

    return false;
  }

  @cached
  get expandableBrackets() {
    if (this.valueType === 'array') {
      return {
        open: '[',
        close: ']',
      };
    }

    // @ts-ignore typeOf isn't typed correctly
    if (this.valueType === 'object') {
      return {
        open: '{',
        close: '}',
      };
    }

    return null;
  }

  @cached
  get items() {
    if (this.valueType === 'array') {
      return (this.args.item.value as unknown[]).map((item, index) => ({ key: index, value: item, isArrayItem: true }));
    }

    // @ts-ignore typeOf isn't typed correctly
    if (this.valueType === 'object') {
      return Object.entries(this.args.item.value).map(([key, value]) => {
        return {
          key,
          value,
        };
      });
    }

    return [];
  }

  @cached
  get arrayValues() {
    if (this.valueType !== 'array') {
      return [];
    }

    return this.args.item.value;
  }

  @cached
  get path() {
    let path: (string | number)[] = [];

    if (this.args.path != null) {
      path = [...this.args.path, this.args.item.key];
    } else {
      path = [this.args.item.key];
    }

    return path;
  }

  @cached
  get pathString() {
    return keyPathToString(this.path);
  }

  @cached
  get isPathUsed() {
    return this.args.pathsUsed.some((pathString) => pathString === this.pathString);
  }

  get canClick() {
    return this.args.depth > 0 && !this.isPathUsed && this.args.canClickItem;
  }

  canClickCopy = this.canClick;

  @action
  onRowClick() {
    if (this.args.mouseEvent !== 'click') {
      return;
    }

    if (!this.canClickCopy) {
      return;
    }

    this.args.onItemClick(this.pathString);
  }

  @action
  onRowMouseDown(event: MouseEvent) {
    if (!this.canClick) {
      this.canClickCopy = this.canClick;
      return;
    }

    if (this.args.mouseEvent === 'mousedown') {
      this.args.onItemClick(this.pathString, event);
      return;
    }

    // A mousedown event might cause focused inputs/CioAceEditor components to
    // lose focus, which in turn will disable the row click functionality (even
    // as the click is happening).
    // We save the current value of "canClick" at the time of the mousedown to
    // check whether the row click (when the mouseup happens) can proceed
    this.canClickCopy = this.canClick;
  }

  @action
  onToggleClick(event: MouseEvent) {
    if (this.args.mouseEvent !== 'click') {
      return;
    }

    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
  }

  @action
  onToggleMouseDown(event: MouseEvent) {
    if (this.args.mouseEvent !== 'mousedown') {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    this.isExpanded = !this.isExpanded;
  }
}
